import {
    RATE_LIMIT_BACKOFF_FACTOR,
    RATE_LIMIT_BASE_DELAY_MS,
    RATE_LIMIT_JITTER,
    RATE_LIMIT_MAX_DELAY_MS,
    RATE_LIMIT_MAX_RETRY_AFTER_MS,
} from './constants';
import { createAbortError } from './errors';

const MS_PER_SECOND = 1000;

export const retryDelayMs = (
    retryAfter: string | null,
    attempt: number,
    random: () => number = Math.random,
): number => {
    const seconds = retryAfter === null || retryAfter.trim() === '' ? NaN : Number(retryAfter);
    const base = Number.isFinite(seconds) && seconds >= 0
        ? Math.min(seconds * MS_PER_SECOND, RATE_LIMIT_MAX_RETRY_AFTER_MS)
        : Math.min(RATE_LIMIT_BASE_DELAY_MS * RATE_LIMIT_BACKOFF_FACTOR ** attempt, RATE_LIMIT_MAX_DELAY_MS);
    return Math.round(base * (1 + RATE_LIMIT_JITTER * random()));
};

interface Waiter {
    timer: number;
    cancel: () => void;
}

export default class RateGate {
    private pausedUntil = 0;

    private readonly waiters = new Set<Waiter>();

    pause(ms: number): void {
        this.pausedUntil = Math.max(this.pausedUntil, Date.now() + ms);
    }

    async wait(signal?: AbortSignal): Promise<void> {
        const remaining = this.pausedUntil - Date.now();
        if (remaining > 0) {
            await this.sleep(remaining, signal);
            await this.wait(signal);
        }
    }

    cancel(): void {
        this.pausedUntil = 0;
        Array.from(this.waiters).forEach((waiter) => waiter.cancel());
    }

    private sleep(ms: number, signal?: AbortSignal): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            if (signal?.aborted) {
                reject(createAbortError());
                return;
            }
            const waiter: Waiter = { timer: 0, cancel: () => undefined };
            const done = (): void => {
                window.clearTimeout(waiter.timer);
                signal?.removeEventListener('abort', waiter.cancel);
                this.waiters.delete(waiter);
            };
            waiter.cancel = () => {
                done();
                reject(createAbortError());
            };
            waiter.timer = window.setTimeout(() => {
                done();
                resolve();
            }, ms);
            signal?.addEventListener('abort', waiter.cancel, { once: true });
            this.waiters.add(waiter);
        });
    }
}
